import { Avatar, Input, Popover, PropertyValue } from '@affine/component';
import {
  type PublicUserInfo,
  PublicUserService,
} from '@affine/core/modules/cloud';
import { MemberSearchService } from '@affine/core/modules/permissions';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DatabaseCellRendererProps } from '../../../types';
import * as styles from './member.css';

interface MemberPreviewItemProps {
  memberId: string;
  onRemove?: () => void;
}

const MemberPreviewItem = ({ memberId, onRemove }: MemberPreviewItemProps) => {
  const publicUserService = useService(PublicUserService);
  const userInfo = useLiveData(publicUserService.publicUser$(memberId));

  // Trigger fetch if not loaded
  useEffect(() => {
    publicUserService.revalidate(memberId);
  }, [publicUserService, memberId]);

  if (!userInfo) {
    return (
      <div className={styles.memberPreview}>
        <Avatar size={20} />
        <span className={styles.memberName}>Loading...</span>
      </div>
    );
  }

  const isRemoved = 'removed' in userInfo && userInfo.removed;

  return (
    <div className={styles.memberPreview}>
      <Avatar
        name={isRemoved ? undefined : (userInfo.name ?? undefined)}
        url={!isRemoved ? (userInfo.avatarUrl ?? userInfo.avatar) : undefined}
        size={20}
      />
      <span className={styles.memberName}>
        {isRemoved ? 'Deleted user' : userInfo.name || 'Unnamed'}
      </span>
      {onRemove && (
        <span
          className={styles.memberRemove}
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
        >
          ×
        </span>
      )}
    </div>
  );
};

interface MemberSearchItemProps {
  member: PublicUserInfo;
  isSelected: boolean;
  onSelect: () => void;
}

const MemberSearchItem = ({
  member,
  isSelected,
  onSelect,
}: MemberSearchItemProps) => {
  const isRemoved = 'removed' in member && member.removed;
  if (isRemoved) return null;

  return (
    <div
      className={styles.searchItem}
      data-selected={isSelected}
      onClick={onSelect}
    >
      <Avatar
        name={member.name ?? undefined}
        url={member.avatarUrl ?? member.avatar ?? undefined}
        size={24}
      />
      <span className={styles.searchItemName}>
        {member.name || 'Unnamed'}
      </span>
      {isSelected && <span className={styles.checkmark}>✓</span>}
    </div>
  );
};

interface MemberSelectPopoverProps {
  selectedIds: string[];
  onSelect: (id: string) => void;
  onDeselect: (id: string) => void;
}

const MemberSelectPopover = ({
  selectedIds,
  onSelect,
  onDeselect,
}: MemberSelectPopoverProps) => {
  const memberSearchService = useService(MemberSearchService);
  const inputRef = useRef<HTMLInputElement>(null);

  const members = useLiveData(memberSearchService.result$);
  const isLoading = useLiveData(memberSearchService.isLoading$);
  const hasMore = useLiveData(memberSearchService.hasMore$);

  // Initialize search on mount
  useEffect(() => {
    memberSearchService.search('');
    return () => {
      memberSearchService.reset();
    };
  }, [memberSearchService]);

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleSearch = useCallback(
    (value: string) => {
      memberSearchService.search(value);
    },
    [memberSearchService]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 50;
      if (nearBottom && hasMore && !isLoading) {
        memberSearchService.loadMore();
      }
    },
    [memberSearchService, hasMore, isLoading]
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <div className={styles.popoverInner}>
      <div className={styles.searchInputWrapper}>
        <Input
          ref={inputRef}
          placeholder="Search members..."
          onChange={handleSearch}
          className={styles.searchInput}
        />
      </div>
      <div className={styles.memberSearchList} onScroll={handleScroll}>
        {members.map(member => (
          <MemberSearchItem
            key={member.id}
            member={{
              id: member.id,
              name: member.name ?? undefined,
              avatar: member.avatarUrl ?? undefined,
              avatarUrl: member.avatarUrl ?? undefined,
            }}
            isSelected={selectedSet.has(member.id)}
            onSelect={() => {
              if (selectedSet.has(member.id)) {
                onDeselect(member.id);
              } else {
                onSelect(member.id);
              }
            }}
          />
        ))}
        {isLoading && (
          <div className={styles.loadingText}>Loading...</div>
        )}
        {!isLoading && members.length === 0 && (
          <div className={styles.emptyText}>No members found</div>
        )}
      </div>
    </div>
  );
};

export const MemberCell = ({
  cell,
  dataSource,
  rowId,
  onChange,
}: DatabaseCellRendererProps) => {
  const [open, setOpen] = useState(false);
  const memberIds = useLiveData(cell.value$ as any) as string[] | undefined;

  // Check if MemberSearchService is available (cloud workspace only)
  let memberSearchService: MemberSearchService | null = null;
  try {
    memberSearchService = useService(MemberSearchService);
  } catch {
    // Service not available in local workspace
  }

  const handleChange = useCallback(
    (newIds: string[]) => {
      dataSource.cellValueChange(rowId, cell.property.id, newIds);
      onChange(newIds);
    },
    [dataSource, rowId, cell.property.id, onChange]
  );

  const handleSelect = useCallback(
    (memberId: string) => {
      const currentIds = memberIds ?? [];
      if (!currentIds.includes(memberId)) {
        handleChange([...currentIds, memberId]);
      }
    },
    [memberIds, handleChange]
  );

  const handleDeselect = useCallback(
    (memberId: string) => {
      const newIds = (memberIds ?? []).filter(id => id !== memberId);
      handleChange(newIds);
    },
    [memberIds, handleChange]
  );

  const isEmpty = !memberIds || memberIds.length === 0;

  // If member search service not available, show cloud-only message
  if (!memberSearchService) {
    return (
      <PropertyValue isEmpty={isEmpty} className={styles.container}>
        <div className={styles.cloudOnly}>
          Members only available in cloud workspace
        </div>
      </PropertyValue>
    );
  }

  return (
    <PropertyValue isEmpty={isEmpty} className={styles.container}>
      <Popover
        open={open}
        onOpenChange={setOpen}
        content={
          <MemberSelectPopover
            selectedIds={memberIds ?? []}
            onSelect={handleSelect}
            onDeselect={handleDeselect}
          />
        }
        contentOptions={{
          className: styles.popoverContent,
        }}
      >
        <button
          type="button"
          className={styles.memberList}
        >
          {isEmpty ? (
            <span className={styles.placeholder}>
              <span className={styles.atSymbol}>@</span> Click to add members...
            </span>
          ) : (
            <>
              {memberIds.map(memberId => (
                <MemberPreviewItem
                  key={memberId}
                  memberId={memberId}
                  onRemove={() => handleDeselect(memberId)}
                />
              ))}
              <span className={styles.addMore}>+ Add</span>
            </>
          )}
        </button>
      </Popover>
    </PropertyValue>
  );
};
